import joi from 'joi';
import { z } from 'zod';
import { htmlSanitize } from '../utils/html-sanitize';

export type TemplateVersionSchema = Record<string, any>;

export interface TemplateCustomCode {
  css?: string;
  js?: string;
}

export type TemplateWidget = Record<string, unknown>;

export interface GetTemplateWidgetsByTypeQuery {
  widget_type: string;
}

export interface GetTemplateWidgetsResponse {
  accountId: string;
  programId: string;
  widgets: TemplateWidget[];
}

export interface TemplateVersionAttributes {
  id?: string;
  accountId?: string;
  programId?: string;
  name?: string | null;
  description?: string | null;
  schema: TemplateVersionSchema;
  theme: any;
  styles: string;
  js?: string | null;
  customCode?: TemplateCustomCode;
  scheduledPublicationDate?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export const $customCode = joi.object<TemplateCustomCode>({
  css: joi
    .string()
    .allow('')
    .custom((value, helpers) => {
      const css = htmlSanitize(value, { allowedTags: [] });
      if (css !== value) {
        return helpers.error('css.invalid');
      }
      return css;
    })
    .optional(),
  js: joi
    .string()
    .allow('')
    .custom((value, helpers) => {
      if (!value) return value;
      // Prevent </script> tags which would break HTML when inserted into a <script> tag
      // Also prevent <script> tags since the code is already inside a script tag
      // Use case-insensitive regex to catch all variations
      if (/<\/script>/i.test(value) || /<script[\s>]/i.test(value)) {
        return helpers.error('js.invalid');
      }
      return value;
    })
    .optional(),
});

interface EditorThemeText {
  fontFamily: string;
  fontSize: number;
  fontWeight: string;
  color: string;
}

export interface BackgroundImageSettings {
  url: string;
  repeat?: boolean;
  position?: string;
  size?: string;
}

export interface EditorTheme {
  global: {
    backgroundColor: string;
    primaryColor: string;
    secondaryColor: string;
    blocksColor: string;
    globalRadius: number;
    imageRadius: number;
    backgroundImage?: BackgroundImageSettings;
  };
  text: {
    paragraph: EditorThemeText & {
      linkColor: string;
    };
    title: EditorThemeText;
    subtitle: EditorThemeText;
  };
  button: EditorThemeText & {
    backgroundColor: string;
    borderRadius: number;
  };
  progressBar: {
    backgroundColor: string;
    progressColor: string;
  };
  favicon?: string;
}

export const $saveTemplateVersionBody = joi.object<SaveTemplateVersionBody>({
  schema: joi.object().pattern(joi.string(), joi.any()).required(),
  theme: joi.object().pattern(joi.string(), joi.any()).required(),
  styles: joi.string().optional().empty(''),
  js: joi.string().optional().empty(''),
  customCode: $customCode.optional().allow(null),
});

export const $updateTemplateNameBody = z.object({
  name: z.string(),
  description: z.string().optional(),
});

export type UpdateTemplateNameBody = z.infer<typeof $updateTemplateNameBody>;

export interface SaveTemplateVersionBody {
  schema: TemplateVersionSchema;
  theme: EditorTheme;
  styles?: string;
  js?: string;
  customCode?: TemplateCustomCode;
  favicon?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface FontAttributes {
  id: string;
  accountId: string;
  type: 'google' | 'custom';
  name: string;
  settings: GoogleFontSettings | CustomFontSettings;
}

export interface GoogleFontSettings {
  link: string;
  cssRule: string;
}

export interface CustomFontSettings {
  ttf: string;
  otf: string;
  woff: string;
}
